import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Check if src is an absolute URL.
 *
 * @param {string} url - Url path
 * @returns {boolean} - true/false value
 */
export const isAbsoluteUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const checkIfImage = (contentType?: string) => {
  return contentType?.startsWith("image/");
};

/**
 * Calculate image position offset in pixels based on focal point for object-position CSS property.
 * This function calculates the pixel offset needed to center the focal point within the image container.
 *
 * @param {number} imageWidth - Current rendered image width in pixels
 * @param {number} imageTotalWidth - Original/total image width in pixels
 * @param {number} focalPointX - Focal point X coordinate in the original image
 * @param {number} imageHeight - Current rendered image height in pixels
 * @param {number} imageTotalHeight - Original/total image height in pixels
 * @param {number} focalPointY - Focal point Y coordinate in the original image
 * @returns {string} - CSS object-position value in pixels (e.g., "10px 20px")
 */
export const changeImagePosition = (
  imageWidth?: number,
  imageTotalWidth?: number,
  focalPointX?: number,
  imageHeight?: number,
  imageTotalHeight?: number,
  focalPointY?: number,
) => {
  let objectPositionX,
    objectPositionY = 0;
  if (imageWidth && imageTotalWidth && focalPointX) {
    const focalPositionX = (imageWidth * focalPointX) / imageTotalWidth;
    objectPositionX = imageWidth / 2 - focalPositionX;
  }

  if (imageHeight && imageTotalHeight && focalPointY) {
    const focalPositionY = (imageHeight * focalPointY) / imageTotalHeight;
    objectPositionY = imageHeight / 2 - focalPositionY;
  }
  return `${objectPositionX}px ${objectPositionY}px`;
};

/**
 * Get Text Colour based on the input
 *
 * @param {string} colorName - Url path
 * @returns {string} - text colour value
 */
export const getTextColourBasedOnBackground = (colorName: string): string => {
  // Define the color names that should return white text
  const whiteTextColors = ["blue", "brown", "slate"];

  // Check if the input color is one of the colors that should return white text
  if (whiteTextColors.includes(colorName.toLowerCase())) {
    return "text-core-white";
  }

  return "text-core-black";
};
